import axios from "axios";
import {
  API_ERROR_FALLBACKS,
  FRONTEND_ERROR_CODES,
  FRONTEND_ERROR_MESSAGES,
  FrontendErrorCode,
} from "@/internals/constants/errorCodes";
import { logError } from "@/services/DatadogService/DatadogService";
import { isQrUploadError } from "@/utils/qrSaveErrors";

// Axios codes emitted when the request never reaches the server (cut connection, CORS, offline)
const NETWORK_ERROR_CODES = new Set(["NETWORK_ERROR", "ERR_NETWORK"]);
// Axios codes emitted when the request takes too long or is aborted by timeout
const TIMEOUT_ERROR_CODES = new Set(["ECONNABORTED", "ETIMEDOUT", "ERR_TIMEOUT"]);

export interface ApiErrorResponse {
  message?: string;
  code?: string;
  statusCode?: number;
  occurredOn?: string;
  [key: string]: any;
}

// Toast message builder
export interface ErrorMessageOptions {
  showDetails?: boolean;
  includeTimestamp?: boolean;
  includeCode?: boolean;
  fallbackMessage?: string;
}

/**
 * Error thrown by the Axios interceptor when a 401 triggers
 * a forced logout. Layerss that catch errors (handleAxiosError, useApi)
 * check for this type and skip logging / toast display.
 */
export class ForceLogoutError extends Error {
  constructor() {
    super("Session expired");
    this.name = "ForceLogoutError";
  }
}

export const isForceLogoutError = (error: unknown): error is ForceLogoutError =>
  error instanceof ForceLogoutError;

/**
 * Custom class to deliver error metadata
 * without losing the JavaScript trace.
 */
export class ApiError extends Error {
  statusCode?: number;
  data?: ApiErrorResponse;

  constructor(message: string, statusCode?: number, data?: ApiErrorResponse) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.data = data;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

/**
 * Generic function to handle Axios errors.
 */
export const handleAxiosError = (error: unknown) => {
  if (isForceLogoutError(error)) throw error;

  if (axios.isAxiosError(error)) {
    const { response, config } = error;

    if (response) {
      const { status, data } = response;
      const { message = "An error occurred while processing your request." }: ApiErrorResponse =
        data || {};

      console.error(`[API Error] Status: ${status}, Data:`, data);
      console.error(`Request URL: ${config?.url}, Method: ${config?.method}`);

      switch (status) {
        case 400:
          throw new ApiError(message || "Bad request. Please try again.", status, data);
        case 401:
          throw new ApiError("Unauthorized. Please log in again.", status, data);
        case 403:
          throw new ApiError("Forbidden. You don't have permission.", status, data);
        case 404:
          throw new ApiError("Resource not found.", status, data);
        case 500:
          throw new ApiError("Server error. Please try again later.", status, data);
        default:
          throw new ApiError(message || "An unknown API error occurred.", status, data);
      }
    } else {
      // Timeout or Network errors
      console.error("[Unexpected Error]", error);
      const networkMsg =
        error.code === "ECONNABORTED" ? "Request timeout" : "Network error or server unreachable";

      throw new ApiError(`${networkMsg} (${error.code || "UNKNOWN"})`, 0, { code: error.code });
    }
  } else {
    // Non-Axios errors (e.g., logic errors)
    console.error("[Non-Axios Error]", error);
    const message = error instanceof Error ? error.message : "An unknown logic error occurred.";

    // Log to Datadog
    if (error instanceof Error) {
      logError(error, { type: "CLIENT_LOGIC_ERROR" });
    }

    throw new ApiError(message, 500, { code: "CLIENT_LOGIC_ERROR" });
  }
};

export class ApiErrorMessageBuilder {
  static buildToastMessage(
    error: any,
    options: ErrorMessageOptions = {
      showDetails: false,
      includeTimestamp: true,
      includeCode: false,
      fallbackMessage: "Something went wrong",
    }
  ): string {
    // 0. File upload failure before the save request (image, video, audio, logo)
    if (isQrUploadError(error)) {
      return FRONTEND_ERROR_MESSAGES[FRONTEND_ERROR_CODES.UPLOAD_FAILED];
    }

    // 1. If it's a pure Axios error (not processed by handleAxiosError)
    if (axios.isAxiosError(error)) {
      return this.handleAxiosErrorForToast(error, options);
    }

    // 2. If it's our ApiError (the one that comes from the switch or the else)
    if (error instanceof ApiError) {
      return this.handleApiErrorResponseForToast(
        {
          message: error.message,
          statusCode: error.statusCode,
          code: error.data?.code,
          occurredOn: error.data?.occurredOn,
        },
        options
      );
    }

    // 3. If we receive an object that looks like an error response but is not an instance of ApiError
    if (error && typeof error === "object") {
      if (error instanceof XMLHttpRequest || (error.status === 0 && !error.message)) {
        return API_ERROR_FALLBACKS.GENERAL.NETWORK;
      }

      const finalMessage = error.message || error.data?.message || error.statusText;
      const finalCode = error.code || error.data?.code || error.status;

      if (finalMessage || finalCode) {
        return this.handleApiErrorResponseForToast(
          {
            message: finalMessage || API_ERROR_FALLBACKS.GENERAL.DEFAULT,
            code: String(finalCode || "unknown"),
            statusCode: error.status || error.statusCode,
          },
          options
        );
      }

      const keys = Object.keys(error).join(", ");
      return `${options.fallbackMessage} (Technical info: ${keys || "No details available"})`;
    }
    // 4. if it's a native JS Error
    if (error instanceof Error) {
      return error.message;
    }

    return options.fallbackMessage;
  }

  // Build toast message for frontend errors (non-API errors)
  static buildFrontendErrorMessage(
    errorCode: FrontendErrorCode,
    originalError?: any,
    customMessage?: string
  ): string {
    const baseMessage = customMessage || FRONTEND_ERROR_MESSAGES[errorCode] || "An error occurred";
    const timestamp = new Date().toLocaleTimeString();

    let errorDetails = "";
    if (originalError) {
      if (originalError instanceof Error) {
        errorDetails = originalError.message;
      } else if (typeof originalError === "string") {
        errorDetails = originalError;
      }
    }
    let message = baseMessage;
    if (errorDetails) message += ` (${errorDetails})`;

    return `${message} | Code: ${errorCode} | Time: ${timestamp}`;
  }

  private static handleAxiosErrorForToast(error: any, options: ErrorMessageOptions): string {
    const { response } = error;
    const fallbackMessage = options.fallbackMessage;

    if (response && response.data) {
      return this.handleApiErrorResponseForToast(response.data, options);
    }

    if (NETWORK_ERROR_CODES.has(error.code)) return API_ERROR_FALLBACKS.GENERAL.NETWORK;

    if (TIMEOUT_ERROR_CODES.has(error.code)) return API_ERROR_FALLBACKS.GENERAL.TIMEOUT;

    return fallbackMessage;
  }

  private static handleApiErrorResponseForToast(
    errorData: ApiErrorResponse,
    options: ErrorMessageOptions
  ): string {
    const { showDetails, includeTimestamp, includeCode, fallbackMessage } = options;

    const message = errorData.message || fallbackMessage || "Unknown Error";
    const messageParts: string[] = [message];

    if (showDetails) {
      const code = errorData.code || "unknown_error";
      const statusCode = errorData.statusCode;
      const occurredOn = errorData.occurredOn;

      if (includeCode && code !== "unknown_error") messageParts.push(`Code: ${code}`);
      if (includeTimestamp && occurredOn) messageParts.push(`Date: ${occurredOn}`);
      if (statusCode) messageParts.push(`Status: ${statusCode}`);
    }

    return messageParts.join(" | ");
  }
}
